import {
    NewRoutedItemTypesParameters,
    NewRoutingRule,
    NewRoutingRuleResult,
    RoutingRuleAccountingCategoryAssignment,
    RoutingRuleProductAssignment,
    RoutingRuleProductCategoryAssignment,
    RoutingRuleRateAssignment,
    Service,
} from '@mews-commander/data-access';
import { useDateTimeLocalizationInfo, useFeatureFlag, useLocalizeProperty } from '@mews/framework';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { FeatureFlag } from '@mews-commander/enums';
import {
    useAccountingCategoriesSuspenseQuery,
    useProductCategoriesSuspenseQuery,
    useProductsSuspenseQuery,
    useRatesSuspenseQuery,
    useServicesSuspenseQuery,
} from '../itemsAndConditions/queries';
import {
    AdditionalServiceOptionsNames,
    AdditionalServicesFormValues,
    CustomItemsOptionsNames,
    OptionsNames,
    ProductsFormValues,
    ProductsOptionsNames,
    RatesOptionsNames,
    RoutingRuleFormValues,
    ServicesFormValues,
} from '../../../../schemas';
import {
    getRoutedItemTypesForAdditionalService,
    getRoutedItemTypesForBookableService,
    useRoutingRuleFromFormValues,
} from '../../../../utils/formValues';
import { useRoutingRuleIdContext } from '../../../../contexts/RoutingRuleIdContext';
import { useRoutingRulesData } from '../../../../queries';

const useProductAssignments = (
    enabledBookableServices: [string, ServicesFormValues[keyof ServicesFormValues]][],
    enabledAdditionalServices: [string, AdditionalServicesFormValues[keyof AdditionalServicesFormValues]][]
) => {
    const productsQuery = useProductsSuspenseQuery();
    const localizeProperty = useLocalizeProperty();

    const getProducts = ({
        serviceProducts,
        isProductsOptionSelected,
        key,
    }: {
        serviceProducts: ProductsFormValues;
        isProductsOptionSelected: boolean;
        key: string;
    }) => {
        if (
            isProductsOptionSelected &&
            serviceProducts &&
            serviceProducts.type === ProductsOptionsNames.SelectedProducts &&
            serviceProducts.selectedProducts?.length
        ) {
            return serviceProducts.selectedProducts.map((productId) => {
                const productNames =
                    productsQuery.data?.products?.find((product) => product.id === productId)?.names || {};
                return {
                    id: productId,
                    routingRuleAssignmentId: key,
                    productId,
                    productName: localizeProperty(productNames),
                    isActive: true,
                };
            });
        }
        return [];
    };

    const bookableServicesProductAssignments = enabledBookableServices.reduce(
        (acc: RoutingRuleProductAssignment[], [key, value]) => {
            const { serviceProducts, serviceOptions } = value;
            const isProductsOptionSelected = serviceOptions.includes(OptionsNames.Products);
            const products = getProducts({ serviceProducts, isProductsOptionSelected, key });
            return [...acc, ...products];
        },
        []
    );
    const additionalServicesProductAssignments = enabledAdditionalServices.reduce(
        (acc: RoutingRuleProductAssignment[], [key, value]) => {
            const { serviceProducts, serviceOptions } = value;
            const isProductsOptionSelected = serviceOptions.includes(AdditionalServiceOptionsNames.Products);
            const products = getProducts({ serviceProducts, isProductsOptionSelected, key });
            return [...acc, ...products];
        },
        []
    );
    return [...bookableServicesProductAssignments, ...additionalServicesProductAssignments];
};

const useProductCategoryAssignments = (
    enabledBookableServices: [string, ServicesFormValues[keyof ServicesFormValues]][],
    enabledAdditionalServices: [string, AdditionalServicesFormValues[keyof AdditionalServicesFormValues]][]
) => {
    const productCategoriesQuery = useProductCategoriesSuspenseQuery();
    const localizeProperty = useLocalizeProperty();

    const getProductCategories = ({
        serviceProducts,
        isProductsOptionSelected,
        key,
    }: {
        serviceProducts: ProductsFormValues;
        isProductsOptionSelected: boolean;
        key: string;
    }) => {
        if (
            isProductsOptionSelected &&
            serviceProducts &&
            serviceProducts.type === ProductsOptionsNames.SelectedProductCategories &&
            serviceProducts.selectedProductCategories?.length
        ) {
            return serviceProducts.selectedProductCategories.map((productCategoryId) => {
                const productCategoryNames =
                    productCategoriesQuery.data?.productCategories?.find(
                        (productCategory) => productCategory.id === productCategoryId
                    )?.names || {};
                return {
                    id: productCategoryId,
                    routingRuleAssignmentId: key,
                    productCategoryId,
                    productCategoryName: localizeProperty(productCategoryNames),
                    isActive: true,
                };
            });
        }
        return [];
    };

    const bookableServicesProductCategoryAssignments = enabledBookableServices.reduce(
        (acc: RoutingRuleProductCategoryAssignment[], [key, value]) => {
            const { serviceProducts, serviceOptions } = value;
            const isProductsOptionSelected = serviceOptions.includes(OptionsNames.Products);
            const productCategories = getProductCategories({ serviceProducts, isProductsOptionSelected, key });
            return [...acc, ...productCategories];
        },
        []
    );
    const additionalServicesProductCategoryAssignments = enabledAdditionalServices.reduce(
        (acc: RoutingRuleProductCategoryAssignment[], [key, value]) => {
            const { serviceProducts, serviceOptions } = value;
            const isProductsOptionSelected = serviceOptions.includes(AdditionalServiceOptionsNames.Products);
            const productCategories = getProductCategories({ serviceProducts, isProductsOptionSelected, key });
            return [...acc, ...productCategories];
        },
        []
    );
    return [...bookableServicesProductCategoryAssignments, ...additionalServicesProductCategoryAssignments];
};

const useRoutingRuleAssignments = (
    enabledBookableServices: [string, ServicesFormValues[keyof ServicesFormValues]][],
    enabledAdditionalServices: [string, AdditionalServicesFormValues[keyof AdditionalServicesFormValues]][]
) => {
    const servicesQuery = useServicesSuspenseQuery();
    const localizeProperty = useLocalizeProperty();

    const getRoutingRuleAssignments = ({
        servicesFromQuery,
        routedItemTypes,
        key,
    }: {
        servicesFromQuery: Service[];
        routedItemTypes: NewRoutedItemTypesParameters;
        key: string;
    }) => {
        const serviceNames = servicesFromQuery?.find((service) => service.id === key)?.names || {};
        return {
            id: key,
            routingRuleId: 'id',
            serviceId: key,
            serviceName: localizeProperty(serviceNames),
            routedItemTypes,
            isRoutingAll: false,
        };
    };

    const bookableServicesRoutingRuleAssignments = enabledBookableServices.map(([key, value]) =>
        getRoutingRuleAssignments({
            servicesFromQuery: servicesQuery.data.bookableServices,
            routedItemTypes: getRoutedItemTypesForBookableService(value),
            key,
        })
    );
    const additionalServicesRoutingRuleAssignments = enabledAdditionalServices.map(([key, value]) =>
        getRoutingRuleAssignments({
            servicesFromQuery: servicesQuery.data.additionalServices,
            routedItemTypes: getRoutedItemTypesForAdditionalService(value),
            key,
        })
    );
    return [...bookableServicesRoutingRuleAssignments, ...additionalServicesRoutingRuleAssignments];
};

export const useAccountingCategoryAssignments = (
    enabledAdditionalServices: [string, AdditionalServicesFormValues[keyof AdditionalServicesFormValues]][]
) => {
    const accountingCategoriesQuery = useAccountingCategoriesSuspenseQuery();
    return enabledAdditionalServices.reduce((acc: RoutingRuleAccountingCategoryAssignment[], [key, value]) => {
        const { serviceProducts, serviceOptions, customItems } = value;
        const isProductsOptionSelected = serviceOptions.includes(AdditionalServiceOptionsNames.Products);
        const isCustomItemsOptionSelected = serviceOptions.includes(AdditionalServiceOptionsNames.CustomItems);

        const productAccountingCategories =
            isProductsOptionSelected &&
            serviceProducts?.type === ProductsOptionsNames.SelectedAccountingCategories &&
            serviceProducts?.selectedAccountingCategories?.length
                ? serviceProducts.selectedAccountingCategories
                : null;
        const customItemAccountingCategories =
            isCustomItemsOptionSelected &&
            customItems?.type === CustomItemsOptionsNames.SelectedAccountingCategories &&
            customItems?.selectedAccountingCategories?.length
                ? customItems.selectedAccountingCategories
                : null;
        const accountingCategories =
            (productAccountingCategories || customItemAccountingCategories)?.map((accountingCategoryId: string) => ({
                id: accountingCategoryId,
                routingRuleAssignmentId: key,
                accountingCategoryId,
                accountingCategoryName:
                    accountingCategoriesQuery.data?.find(
                        (accountingCategory) => accountingCategory.id === accountingCategoryId
                    )?.name || '',
                itemTypes: {
                    products: !!productAccountingCategories?.length,
                    customItems: !!customItemAccountingCategories?.length,
                },
                isActive: true,
            })) ?? [];
        return [...acc, ...accountingCategories];
    }, []);
};

export const useMapFormValuesToRoutingRule = (formValues: RoutingRuleFormValues): NewRoutingRuleResult => {
    const recurringRoutingRulesEnabled = useFeatureFlag(FeatureFlag.recurringRoutingRulesEnabled);
    const granularRoutingRulesConditionsExtensionEnabled = useFeatureFlag(
        FeatureFlag.granularRoutingRulesConditionsExtensionEnabled
    );
    const localizationInfo = useDateTimeLocalizationInfo();
    const routingRule = useRoutingRuleFromFormValues(
        formValues,
        recurringRoutingRulesEnabled,
        localizationInfo,
        granularRoutingRulesConditionsExtensionEnabled
    );
    const { itemsAndConditions } = formValues;
    const { services, additionalServices, ratesEnabled } = itemsAndConditions;

    const enterpriseId = useCurrentEnterpriseId();
    const ratesQuery = useRatesSuspenseQuery();

    const { routingRuleId } = useRoutingRuleIdContext();
    const { data: routingRuleResult } = useRoutingRulesData(routingRuleId);

    const enabledBookableServices = Object.entries(services).filter(([_, value]) => value.serviceEnabled);
    const enabledAdditionalServices = additionalServices
        ? Object.entries(additionalServices).filter(([_, value]) => value.serviceEnabled)
        : [];

    const rateAssignments = enabledBookableServices.reduce((acc: RoutingRuleRateAssignment[], [key, value]) => {
        const { serviceRates } = value;
        if (serviceRates && serviceRates.selectedRates && serviceRates.type === RatesOptionsNames.Selected) {
            const rates = serviceRates.selectedRates.map((rateId: string) => {
                const isDeletedRate = routingRuleResult?.rateAssignments?.some(
                    (rateAssignment) => rateAssignment.rateId === rateId && !rateAssignment.isActive
                );
                const deletedRateName = isDeletedRate
                    ? routingRuleResult?.rateAssignments?.find(
                          (rateAssignment) => rateAssignment.rateId === rateId && !rateAssignment.isActive
                      )?.rateName
                    : null;
                const rateName =
                    deletedRateName || ratesQuery.data?.rates?.find((rate) => rate.id === rateId)?.name || '';
                return {
                    id: rateId,
                    routingRuleAssignmentId: key,
                    rateId,
                    rateName,
                    isActive: true,
                };
            });
            return [...acc, ...rates];
        }
        return acc;
    }, []);

    return {
        routingRules: [
            {
                ...routingRule,
                id: 'id',
                enterpriseId,
                creatorProfileId: 'id',
                updaterProfileId: 'id',
                createdUtc: '',
                updatedUtc: '',
                isDeleted: false,
            } as NewRoutingRule,
        ],
        routingRuleAssignments: useRoutingRuleAssignments(enabledBookableServices, enabledAdditionalServices),
        rateAssignments: ratesEnabled ? rateAssignments : [],
        productAssignments: useProductAssignments(enabledBookableServices, enabledAdditionalServices),
        productCategoryAssignments: useProductCategoryAssignments(enabledBookableServices, enabledAdditionalServices),
        accountingCategoryAssignments: useAccountingCategoryAssignments(enabledAdditionalServices),
    };
};
